/**
 * Support Chat Hook
 *
 * Manages support chat conversations and SSE streaming for real-time replies.
 *
 * @module
 * @see apps/api/src/support/routes.ts - Backend API routes
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

// ============================================================================
// Types
// ============================================================================

export interface SupportMessageAttachment {
  type: 'image' | 'file';
  name?: string;
  url: string;
  mimeType?: string;
}

export interface SupportMessage {
  id: string;
  conversationId: string;
  senderType: 'user' | 'support';
  senderName: string | null;
  content: string;
  attachments: SupportMessageAttachment[] | null;
  slackTs: string | null;
  createdAt: string;
}

export interface SupportConversation {
  id: string;
  userId: string;
  orgId: string;
  slackTeamId: string;
  slackChannelId: string;
  slackThreadTs: string;
  subject: string | null;
  status: 'open' | 'resolved';
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string;
  messageCount?: number;
}

interface Pagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface SupportStatusResponse {
  ok: boolean;
  data: { enabled: boolean };
}

interface ListConversationsResponse {
  ok: boolean;
  data: SupportConversation[];
  pagination: Pagination;
}

interface ConversationResponse {
  ok: boolean;
  data: SupportConversation;
}

interface ListMessagesResponse {
  ok: boolean;
  data: SupportMessage[];
  pagination: Pagination;
}

interface MessageResponse {
  ok: boolean;
  data: SupportMessage;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchSupportStatus(): Promise<SupportStatusResponse> {
  const res = await fetch(`${API_URL}/support/status`, { credentials: 'include' });
  if (res.status === 401 || res.status === 403) return { ok: true, data: { enabled: false } };
  if (!res.ok) throw await toApiError(res, 'Failed to fetch support status');
  return res.json();
}

async function fetchConversations(
  page = 1,
  pageSize = 20,
  includeResolved = false
): Promise<ListConversationsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    includeResolved: String(includeResolved),
  });
  const res = await fetch(`${API_URL}/support/conversations?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch conversations');
  return res.json();
}

async function fetchMessages(
  conversationId: string,
  page = 1,
  pageSize = 100
): Promise<ListMessagesResponse> {
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const res = await fetch(`${API_URL}/support/conversations/${conversationId}/messages?${params}`, {
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to fetch messages');
  return res.json();
}

async function createConversation(payload: {
  subject?: string;
  initialMessage: string;
  files?: File[];
}): Promise<ConversationResponse> {
  const hasFiles = !!payload.files && payload.files.length > 0;
  let body: BodyInit;
  const headers: Record<string, string> = {};
  if (hasFiles) {
    const form = new FormData();
    if (payload.subject) form.append('subject', payload.subject);
    form.append('initialMessage', payload.initialMessage);
    for (const file of payload.files!) form.append('files', file);
    body = form; // browser sets the multipart boundary; don't set Content-Type
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ subject: payload.subject, initialMessage: payload.initialMessage });
  }

  const res = await fetch(`${API_URL}/support/conversations`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body,
  });
  if (!res.ok) throw await toApiError(res, 'Failed to create conversation');
  return res.json();
}

async function sendMessage(
  conversationId: string,
  content: string,
  files?: File[]
): Promise<MessageResponse> {
  const hasFiles = !!files && files.length > 0;
  let body: BodyInit;
  const headers: Record<string, string> = {};
  if (hasFiles) {
    const form = new FormData();
    form.append('content', content);
    for (const file of files!) form.append('files', file);
    body = form; // browser sets the multipart boundary; don't set Content-Type
  } else {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify({ content });
  }

  const res = await fetch(`${API_URL}/support/conversations/${conversationId}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body,
  });
  if (!res.ok) throw await toApiError(res, 'Failed to send message');
  return res.json();
}

async function resolveConversation(conversationId: string): Promise<ConversationResponse> {
  const res = await fetch(`${API_URL}/support/conversations/${conversationId}/resolve`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw await toApiError(res, 'Failed to resolve conversation');
  return res.json();
}

// ============================================================================
// Query Keys
// ============================================================================

const supportQueryKeys = {
  status: ['support', 'status'] as const,
  conversations: (page: number, includeResolved: boolean) =>
    ['support', 'conversations', { page, includeResolved }] as const,
  conversation: (id: string) => ['support', 'conversation', id] as const,
  messages: (conversationId: string, page: number) =>
    ['support', 'messages', conversationId, { page }] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Check if support chat is enabled for this organization
 */
export function useSupportStatus() {
  return useQuery({
    queryKey: supportQueryKeys.status,
    queryFn: fetchSupportStatus,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * List user's support conversations
 */
function useSupportConversations(page = 1, includeResolved = false) {
  return useQuery({
    queryKey: supportQueryKeys.conversations(page, includeResolved),
    queryFn: () => fetchConversations(page, 20, includeResolved),
  });
}

/**
 * Get messages for a conversation
 */
function useSupportMessages(conversationId: string | null, page = 1) {
  return useQuery({
    queryKey: supportQueryKeys.messages(conversationId ?? '', page),
    queryFn: () => fetchMessages(conversationId!, page),
    enabled: !!conversationId,
  });
}

/**
 * Create a new conversation mutation
 */
function useCreateSupportConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support', 'conversations'] });
    },
  });
}

/**
 * Send a message mutation
 */
function useSendSupportMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      content,
      files,
    }: {
      conversationId: string;
      content: string;
      files?: File[];
    }) => sendMessage(conversationId, content, files),
    onSuccess: (_, { conversationId }) => {
      queryClient.invalidateQueries({
        queryKey: supportQueryKeys.messages(conversationId, 1),
      });
    },
  });
}

/**
 * Resolve a conversation mutation
 */
function useResolveSupportConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resolveConversation,
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['support', 'conversations'] });
      queryClient.invalidateQueries({
        queryKey: supportQueryKeys.conversation(conversationId),
      });
    },
  });
}

// ============================================================================
// SSE Stream Hook
// ============================================================================

interface SupportStreamMessage {
  id: string;
  conversationId: string;
  senderType: 'user' | 'support';
  senderName: string | null;
  content: string;
  attachments: SupportMessageAttachment[] | null;
  createdAt: string;
}

/**
 * Subscribe to SSE stream for real-time message updates
 */
function useSupportMessageStream(conversationId: string | null) {
  const queryClient = useQueryClient();
  const eventSourceRef = React.useRef<EventSource | null>(null);

  React.useEffect(() => {
    if (!conversationId) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      return;
    }

    const eventSource = new EventSource(
      `${API_URL}/support/conversations/${conversationId}/stream`,
      { withCredentials: true }
    );
    eventSourceRef.current = eventSource;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as SupportStreamMessage;

        // Add the new message to the cache
        queryClient.setQueryData<ListMessagesResponse>(
          supportQueryKeys.messages(conversationId, 1),
          (prev) => {
            if (!prev) return prev;

            // Check if message already exists
            const exists = prev.data.some((msg) => msg.id === data.id);
            if (exists) return prev;

            const newMessage: SupportMessage = {
              id: data.id,
              conversationId: data.conversationId,
              senderType: data.senderType,
              senderName: data.senderName,
              content: data.content,
              attachments: data.attachments,
              slackTs: null,
              createdAt: data.createdAt,
            };

            return {
              ...prev,
              data: [...prev.data, newMessage],
            };
          }
        );
      } catch (error) {
        console.error('Failed to parse support message event', error);
      }
    };

    eventSource.addEventListener('support-message', handleMessage as EventListener);

    eventSource.onerror = (err) => {
      console.error('Support message stream error', err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [conversationId, queryClient]);
}

// ============================================================================
// Combined Hook for Chat UI
// ============================================================================

interface UseSupportChatOptions {
  autoCreateConversation?: boolean;
}

interface UseSupportChatResult {
  /** Whether support chat is enabled */
  isEnabled: boolean;
  /** Whether we're checking if support is enabled */
  isCheckingEnabled: boolean;
  /** Current active conversation */
  activeConversation: SupportConversation | null;
  /** All user's conversations */
  conversations: SupportConversation[];
  /** Messages in active conversation */
  messages: SupportMessage[];
  /** Whether messages are loading */
  isLoadingMessages: boolean;
  /** Whether we're sending a message */
  isSending: boolean;
  /** Start a new conversation with an initial message and optional attachments */
  startConversation: (
    subject: string | undefined,
    initialMessage: string,
    files?: File[]
  ) => Promise<void>;
  /** Send a message (and optional attachments) to the active conversation */
  sendMessage: (content: string, files?: File[]) => Promise<void>;
  /** Switch to a different conversation */
  selectConversation: (id: string | null) => void;
  /** Mark the active conversation as resolved */
  resolveConversation: () => Promise<void>;
}

/**
 * Combined hook for support chat UI
 */
export function useSupportChat(_options: UseSupportChatOptions = {}): UseSupportChatResult {
  const [activeConversationId, setActiveConversationId] = React.useState<string | null>(null);

  // Queries
  const statusQuery = useSupportStatus();
  const conversationsQuery = useSupportConversations();
  const messagesQuery = useSupportMessages(activeConversationId);

  // Mutations
  const createMutation = useCreateSupportConversation();
  const sendMutation = useSendSupportMessage();
  const resolveMutation = useResolveSupportConversation();

  // Subscribe to SSE stream for real-time updates
  useSupportMessageStream(activeConversationId);

  // Get active conversation details
  const activeConversation = React.useMemo(() => {
    if (!activeConversationId || !conversationsQuery.data?.data) return null;
    return conversationsQuery.data.data.find((c) => c.id === activeConversationId) ?? null;
  }, [activeConversationId, conversationsQuery.data]);

  // Callbacks
  const startConversation = React.useCallback(
    async (subject: string | undefined, initialMessage: string, files?: File[]) => {
      const result = await createMutation.mutateAsync({ subject, initialMessage, files });
      if (result.ok) {
        setActiveConversationId(result.data.id);
      }
    },
    [createMutation]
  );

  const sendMessageToConversation = React.useCallback(
    async (content: string, files?: File[]) => {
      if (!activeConversationId) {
        throw new Error('No active conversation');
      }
      await sendMutation.mutateAsync({ conversationId: activeConversationId, content, files });
    },
    [activeConversationId, sendMutation]
  );

  const resolveActiveConversation = React.useCallback(async () => {
    if (!activeConversationId) {
      throw new Error('No active conversation');
    }
    await resolveMutation.mutateAsync(activeConversationId);
    setActiveConversationId(null);
  }, [activeConversationId, resolveMutation]);

  return {
    isEnabled: statusQuery.data?.data.enabled ?? false,
    isCheckingEnabled: statusQuery.isLoading,
    activeConversation,
    conversations: conversationsQuery.data?.data ?? [],
    messages: messagesQuery.data?.data ?? [],
    isLoadingMessages: messagesQuery.isLoading,
    isSending: createMutation.isPending || sendMutation.isPending,
    startConversation,
    sendMessage: sendMessageToConversation,
    selectConversation: setActiveConversationId,
    resolveConversation: resolveActiveConversation,
  };
}
